import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { loadSentHistoryForRange, sentHistoryStatus } from '@common-reducers/planner-sent-history';
import { getCalendarVisibleRange, type CalendarViewName } from '../calendar-range';

/**
 * Is what already went out, for the range ON SCREEN, still loading — or did it fail?
 * One source for every indicator that answers "why am I not seeing it yet": the Show sent
 * switch in the filter panel and the centre of the calendar toolbar read the same in-flight
 * requests, so they can never disagree. `retry` asks for the same range again.
 */
export function useSentHistoryStatus() {
    const dispatch = useAppDispatch();
    const filters = useAppSelector(state => state.CalendarReducer.calendarSidebarFilters);
    const currentDate = useAppSelector(state => state.CalendarReducer.currentDate);
    const view = useAppSelector(state => state.CalendarReducer.view);
    const history = useAppSelector(state => state.PlannerSentHistoryReducer);

    const range = useMemo(() => {
        const r = getCalendarVisibleRange(currentDate as any, (view === 'week' || view === 'day' ? view : 'month') as CalendarViewName);
        return { start: new Date(r.start).getTime(), end: new Date(r.end).getTime() };
    }, [currentDate, view]);

    const status = useMemo(() => sentHistoryStatus(filters, range, history), [filters, range, history]);
    const retry = useCallback(
        () => { dispatch(loadSentHistoryForRange({ start: new Date(range.start), end: new Date(range.end) })); },
        [dispatch, range],
    );
    return { ...status, retry };
}
